/**
 * DbPersister — decides when the database gets written to storage.
 *
 * Its single job is timing. Culling fires a change on every keystroke, so
 * writing synchronously would stall navigation; but waiting too long risks
 * losing decisions if the tab is closed or reloaded, because the write is async
 * and a `pagehide` handler cannot await it.
 *
 * So there are two clocks:
 *
 *   delay    — quiet period after the last change. Short, so a pause of even a
 *              moment commits the work.
 *   maxWait  — hard ceiling from the FIRST pending change. Without it, a steady
 *              stream of keystrokes keeps resetting the debounce and nothing is
 *              ever written; that is exactly how a run of culling used to be
 *              lost on reload.
 *
 * Worst case, `maxWait` milliseconds of decisions are at risk. Everything older
 * than that is already on disk.
 */

import type { SqliteClient } from './sqlite-client';
import type { DbStorage } from './db-storage';

export class DbPersister {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private ceiling: ReturnType<typeof setTimeout> | null = null;
  private dirty = false;
  private inFlight: Promise<void> | null = null;

  constructor(
    private readonly client: SqliteClient,
    private readonly storage: DbStorage,
    private readonly delayMs = 150,
    private readonly maxWaitMs = 600,
  ) {}

  /** Note that something changed. Cheap — safe to call on every mutation. */
  touch(): void {
    this.dirty = true;

    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), this.delayMs);

    // Started on the first pending change and never reset, so a continuous
    // stream of edits still commits at a predictable interval.
    this.ceiling ??= setTimeout(() => void this.flush(), this.maxWaitMs);
  }

  /** Write now, if there is anything to write. Awaits any save already running. */
  async flush(): Promise<void> {
    this.clearTimers();
    if (this.inFlight) await this.inFlight;
    if (!this.dirty) return;

    this.dirty = false;
    this.inFlight = this.storage
      .save(this.client.snapshot())
      .catch((err) => {
        this.dirty = true; // keep it queued so the next flush retries
        throw err;
      })
      .finally(() => {
        this.inFlight = null;
      });

    await this.inFlight;
  }

  /**
   * Mark a change and write it immediately.
   *
   * For the rare, deliberate write that must not wait on the debounce — a
   * preference the photographer just set, say. It exists as its own method
   * because `flush()` returns early when nothing is marked dirty, so calling it
   * *instead of* `touch()` silently writes nothing: a trap worth removing
   * rather than documenting.
   */
  async saveNow(): Promise<void> {
    this.dirty = true;
    await this.flush();
  }

  get hasPendingWrites(): boolean {
    return this.dirty;
  }

  private clearTimers(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    if (this.ceiling !== null) clearTimeout(this.ceiling);
    this.timer = null;
    this.ceiling = null;
  }

  dispose(): void {
    this.clearTimers();
  }
}
